import dayjs from 'dayjs';
import React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  TSewBillingCycleRangeExportFormProps,
  TSewBillingCycleRangeExportInputSchema,
} from '@/backoffice/features/data-exports/sew-billing-cycle-range-export/sew-billing-cycle-range-export.types';
import { getLatestExportableBillingCycle } from '@/backoffice/features/data-exports/utils/latest-exportable-billing-cycle';
import { FormMonthYearPicker } from '@/shared/components/ui/form/components/form-date-time-picker/variants/form-month-year-picker';
import { Submit } from '@/shared/components/ui/form/components/submit/submit';
import { FormContent } from '@/shared/components/ui/form/form-content';
import { FormContainer } from '@/shared/components/ui/form/form.styled';
import { SelectDateTrigger } from '@/shared/components/ui/month-year-select/month-year-select.styled';
import { useDateUtils } from '@/shared/hooks/use-date-utils/use-date-utils';

export const SewBillingCycleRangeExportForm: React.FC<
  TSewBillingCycleRangeExportFormProps
> = ({ handleClose }) => {
  const { t } = useTranslation();
  const { formatLocalTime } = useDateUtils();
  const { trigger } = useFormContext<TSewBillingCycleRangeExportInputSchema>();
  const [startBillingCycleTs, endBillingCycleTs] = useWatch({
    name: ['startBillingCycleTs', 'endBillingCycleTs'],
  });

  const monthLimit = {
    maxDate: dayjs.utc(getLatestExportableBillingCycle()),
  };

  const onAccept = () => {
    if (startBillingCycleTs && endBillingCycleTs) {
      void trigger();
    }
  };

  return (
    <FormContainer>
      <FormContent>
        <FormMonthYearPicker<TSewBillingCycleRangeExportInputSchema>
          name="startBillingCycleTs"
          required
          dateTimePickerProps={monthLimit}
          label={t('billing:period_start')}
          triggerElement={(value) => (
            <SelectDateTrigger variant="primary" $hasValue={!!value}>
              {value
                ? formatLocalTime(value, 'UTC', 'MMMM yyyy')
                : t('billing:select_period_start')}
            </SelectDateTrigger>
          )}
          onAccept={onAccept}
        />
        <FormMonthYearPicker<TSewBillingCycleRangeExportInputSchema>
          name="endBillingCycleTs"
          required
          dateTimePickerProps={monthLimit}
          label={t('billing:period_end')}
          triggerElement={(value) => (
            <SelectDateTrigger variant="primary" $hasValue={!!value}>
              {value
                ? formatLocalTime(value, 'UTC', 'MMMM yyyy')
                : t('billing:select_period_end')}
            </SelectDateTrigger>
          )}
          onAccept={onAccept}
        />
      </FormContent>
      <Submit submitButtonLabel={t('common:download')} onClose={handleClose} />
    </FormContainer>
  );
};
